import React, { useRef, useState } from 'react';
import { Input, InputGroup, InputGroupText } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import dayjs, { dateInputFieldFormat, formatDateString } from '~libs/dayjs';
import { useToast } from '~libs/toast';
import {
  getBulkUpdateErrors,
  getRowFlag,
  RowFlag,
  isUpdatedInPlace,
  isValidEffectiveDate,
  parseProportion,
} from '~support-at-home/lib/bulkUpdate';
import { BulkUpdateRow } from '~support-at-home/lib/contributionSelection';

import { Button, ErrorResponse } from '~components';
import DateInput from '~components/form-fields/inputs/DateInput';
import Modal from '~components/Modal';
import { TableView } from '~components/table-view';

import bulkPreviewColumns from '~support-at-home/forms/client-contributions/bulkPreview';

import { BulkUpdateResult, useBulkUpdateContributionsMutation } from '~support-at-home/actions/sahClientContributions';

interface ComponentProps {
  // The selected rows, in list order.
  rows: BulkUpdateRow[];
  // The browser's local date as `YYYY-MM-DD`; the default and earliest effective date.
  today: string;
  onClose: () => void;
  // Called after the API has saved everything, so the page can clear its selection.
  onSuccess: (result: BulkUpdateResult) => void;
}

const plural = (count: number, singular: string, pluralForm = `${singular}s`) =>
  `${count} ${count === 1 ? singular : pluralForm}`;

const Required = () => (
  <span className="text-muted" aria-hidden="true">
    {' '}
    *
  </span>
);

const BulkUpdateContributionsModal: React.FC<ComponentProps> = ({ rows, today, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [bulkUpdate] = useBulkUpdateContributionsMutation();

  const [percent, setPercent] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string | null>(today);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const rateInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>('');
  const [serverErrors, setServerErrors] = useState<ReturnType<typeof getBulkUpdateErrors>>([]);

  const proportion = parseProportion(percent);
  // The API rejects backdated rates too. `minDate` alone doesn't stop a typed date, so gate it here as well.
  // (YYYY-MM-DD strings compare as dates.)
  const isBackdated = isValidEffectiveDate(effectiveDate) && effectiveDate < today;
  const validDate = isValidEffectiveDate(effectiveDate) && !isBackdated ? effectiveDate : null;

  const flags = new Map<string, RowFlag>();
  rows.forEach((row) => {
    const flag = getRowFlag(row, proportion, validDate);
    if (flag) flags.set(row.id, flag);
  });
  const serverReasons = new Map(serverErrors.map((item) => [item.id, item.reason]));

  const canConfirm = proportion !== null && validDate !== null && rows.length > 0 && flags.size === 0 && !isSaving;

  const clearErrors = () => {
    setError('');
    setServerErrors([]);
  };

  const handleConfirm = async () => {
    if (!canConfirm || isSavingRef.current || proportion === null || validDate === null) return;

    // The ref stops a second press landing before the state has re-rendered the button disabled.
    isSavingRef.current = true;
    setIsSaving(true);
    clearErrors();
    try {
      const result = await bulkUpdate({
        ids: rows.map((row) => row.id),
        contrib_proportion: proportion,
        effective_date: validDate,
      }).unwrap();

      // Only the parts that happened, so a run that only started new rates doesn't read "0 updated".
      const outcomes = [
        result?.created ? `${plural(result.created, 'new rate')} created` : '',
        result?.updated ? `${plural(result.updated, 'contribution')} updated` : '',
      ].filter(Boolean);
      showToast('success', `Bulk update applied: ${outcomes.join(', ') || 'no changes'}`);
      onSuccess(result);
    } catch (caught) {
      setError(getErrorMessage(caught));
      setServerErrors(getBulkUpdateErrors(caught));
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  // One line for the whole change, not one per row.
  let summary = isBackdated
    ? `The effective date can't be before today (${formatDateString(today)})`
    : 'Enter a rate and date to preview the change';
  if (proportion !== null && validDate !== null) {
    const inPlace = rows.filter((row) => isUpdatedInPlace(row, validDate)).length;
    const lastDay = formatDateString(dayjs(validDate).subtract(1, 'day').format(dateInputFieldFormat));
    const from = formatDateString(validDate);
    summary =
      inPlace === rows.length
        ? `${plural(inPlace, 'contribution')} start on or after ${from} and will be updated in place`
        : `Current rates end ${lastDay}; new rates start ${from}`;
    if (inPlace > 0 && inPlace < rows.length) summary += ` · ${inPlace} will be updated in place`;
  }

  const flagCount = (kind: RowFlag['kind']) => Array.from(flags.values()).filter((flag) => flag.kind === kind).length;
  const overMax = flagCount('over-max');
  const endsBefore = flagCount('ends-before');
  const itThem = (n: number) => (n === 1 ? 'it' : 'them');
  const exceedAlert = [
    overMax > 0 &&
      `${plural(overMax, 'row')} exceed${overMax === 1 ? 's' : ''} ` +
        `${overMax === 1 ? 'its' : 'their'} category maximum; untick ${itThem(overMax)} or choose a lower rate.`,
    endsBefore > 0 &&
      validDate !== null &&
      `${plural(endsBefore, 'row')} end${endsBefore === 1 ? 's' : ''} before ${formatDateString(validDate)}; ` +
        `untick ${itThem(endsBefore)} or choose an earlier date.`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Modal
      isOpen
      setIsOpen={(open) => {
        if (!open && !isSaving) onClose();
      }}
      className="bulk-update-modal"
      // Open on the field the user has to fill in first. (The modal takes focus itself as it opens, so
      // this runs once it has finished opening.)
      onOpened={() => rateInputRef.current?.focus()}
      header="Bulk Update Contributions"
      disableClose={isSaving}
      bodyClassName="p-3 d-flex flex-column gap-2"
      footer={
        <>
          <Button
            id="sah-client-contributions-bulk-confirm-button"
            color="success"
            loading={isSaving}
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {`Update ${plural(rows.length, 'contribution')}`}
          </Button>
          <Button
            id="sah-client-contributions-bulk-cancel-button"
            color="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </Button>
        </>
      }
    >
      <div className="bulk-update-inputs">
        <div className="bulk-update-field bulk-update-field-rate">
          <label className="form-label small mb-1" htmlFor="bulk_contrib_proportion">
            New rate
            <Required />
          </label>
          <InputGroup>
            <Input
              id="bulk_contrib_proportion"
              name="bulk_contrib_proportion"
              type="number"
              min={0}
              step="any"
              innerRef={rateInputRef}
              aria-required="true"
              value={percent}
              disabled={isSaving}
              data-cy="bulk_contrib_proportion-input"
              onChange={(event) => {
                clearErrors();
                setPercent(event.target.value);
              }}
            />
            <InputGroupText>%</InputGroupText>
          </InputGroup>
        </div>
        <label className="bulk-update-field bulk-update-field-date mb-0">
          <span className="form-label small d-block mb-1">
            Effective from
            <Required />
          </span>
          <DateInput
            type="date"
            id="bulk_effective_date"
            name="bulk_effective_date"
            value={effectiveDate}
            minDate={today}
            disabled={isSaving}
            onChange={(_name: string, value: string | null) => {
              clearErrors();
              setEffectiveDate(value);
            }}
          />
        </label>
      </div>
      <p className="small text-muted mb-0" role="status" data-cy="sah-client-contributions-bulk-summary">
        {summary}
      </p>
      {exceedAlert && (
        <div className="small text-danger" role="alert" data-cy="sah-client-contributions-bulk-exceed-alert">
          {exceedAlert}
        </div>
      )}
      {serverErrors.length > 0 ? (
        <div className="small text-danger" role="alert" data-cy="sah-client-contributions-bulk-server-alert">
          {error}
        </div>
      ) : (
        <ErrorResponse message={error} dataCy="sah-client-contributions-bulk-error" />
      )}
      <div
        className="bulk-update-preview"
        role="region"
        aria-label="Contributions that will change"
        tabIndex={0}
        data-cy="sah-client-contributions-bulk-preview"
      >
        <TableView
          id="sah_bulk_update_preview_table"
          columns={bulkPreviewColumns({ proportion, validDate, flags, serverReasons })}
          data={rows}
          readonly
          compactView
          showAddRow={false}
          rowClassName={(row) =>
            flags.has(String(row.id)) || serverReasons.has(String(row.id)) ? 'is-flagged' : undefined
          }
        />
      </div>
    </Modal>
  );
};

export default BulkUpdateContributionsModal;
